import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Checkbox, DateTimePicker, Dialog, Field, NumberInput, RadioGroup, Segmented, Stack, t, Textarea, toast,
} from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, ApiFailure, causeOf, unwrap } from '../lib/api';
import { clockTime, shortDate } from '../lib/format';
import { pendingLabel } from './PendingButton';

type Maintenance = fleetState.components['schemas']['Maintenance'];
type Results = fleetState.components['schemas']['MaintenanceResults']['results'];
const DURATIONS = ['30', '60', '120', '240'] as const;

export interface MaintenanceTarget {
  name: string;
  environment: string;
  team?: string;
}

/**
 * « Poser une maintenance » (SPEC-004 dialog, grouped variant of SPEC-003):
 * now or planned, a duration, a logged reason; availability alerts are always
 * suspended, security alerts stay on unless unchecked (RG-004-4).
 */
export function MaintenanceDialog({ open, targets, environmentLabel, onClose, onDone, onStepUp }: {
  open: boolean;
  targets: MaintenanceTarget[];
  environmentLabel: (environment: string) => string;
  onClose: () => void;
  onDone: (outcome: { posted: Maintenance[]; rejected: Results }) => void;
  /** Asked for a recent strong authentication (MFA_STEP_UP_REQUIRED). */
  onStepUp: () => void;
}) {
  const bounds = useQuery({
    queryKey: ['fleet-thresholds'],
    queryFn: async () => unwrap(await api.fleetState.GET('/thresholds')),
    staleTime: 600_000,
    enabled: open,
  }).data?.maintenance;
  const [when, setWhen] = useState<'now' | 'planned'>('now');
  const [start, setStart] = useState<Date | null>(null);
  const [duration, setDuration] = useState<string>('60');
  const [other, setOther] = useState<number | null>(90);
  const [reason, setReason] = useState('');
  const [notifyTeam, setNotifyTeam] = useState(true);
  const [keepSecurity, setKeepSecurity] = useState(true);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    if (!open) return;
    setWhen('now'); setStart(null); setDuration('60'); setOther(90); setReason(''); setNotifyTeam(true); setKeepSecurity(true); setFailure(null);
  }, [open]);

  const single = targets.length === 1;
  const minutes = duration === 'other' ? other ?? 0 : Number(duration);
  const reasonMin = bounds?.reasonMin ?? 10;
  const ready = reason.trim().length >= reasonMin && minutes > 0 && (when === 'now' || start !== null);
  const teams = [...new Set(targets.map(x => x.team).filter(Boolean))];
  const fieldError = (field: string) => (failure?.body.details?.fields as { field: string; message: string }[] | undefined)?.find(f => f.field === field)?.message;

  const submit = async () => {
    if (!ready) return;
    setSending(true);
    setFailure(null);
    const draft = { start: when === 'planned' && start ? start.toISOString() : null, durationMinutes: minutes, reason: reason.trim(), notifyTeam, keepSecurityAlerts: keepSecurity };
    try {
      if (single) {
        const m = unwrap(await api.fleetState.POST('/servers/{server}/maintenances', { params: { path: { server: targets[0]!.name } }, body: draft }));
        onDone({ posted: [m], rejected: [] });
        toast({
          tone: 'pos',
          title: m.status === 'planned'
            ? t('maintenance.toast.planned', { date: shortDate(m.start), time: clockTime(m.start), duration: durationLabel(minutes) })
            : t('maintenance.toast.posted', { server: m.server, until: clockTime(m.end) }),
        });
      } else {
        const response = await api.fleetState.POST('/maintenances', { body: { ...draft, servers: targets.map(x => x.name) } });
        // 409 means none posted: each server says why.
        const results = response.data?.results ?? (response.error as { results?: Results } | undefined)?.results;
        if (!results) unwrap(response);
        const posted = results!.filter(r => r.status === 'created');
        const rejected = results!.filter(r => r.status === 'rejected');
        onDone({ posted: [], rejected });
        toast({ tone: posted.length ? 'pos' : 'neg', title: posted.length ? t('maintenance.toast.bulk', { n: posted.length }) : t('maintenance.toast.bulkNone') });
      }
      onClose();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') onStepUp();
      else setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };

  const title = single ? t('maintenance.dialog.title') : t('maintenance.dialog.titleBulk', { n: targets.length });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={title}
      description={single ? `${targets[0]?.name} · ${environmentLabel(targets[0]?.environment ?? '')}` : targets.map(x => x.name).join(', ')}
      onSubmit={e => { e.preventDefault(); void submit(); }}
      confirmLabel={pendingLabel(single ? t('maintenance.dialog.confirm') : t('maintenance.dialog.confirmBulk', { n: targets.length }), sending)}
      confirmDisabled={!ready || sending}
      cancelLabel={t('maintenance.dialog.cancel')}
    >
      <Stack gap={16}>
        {failure && (
          <Alert tone={failure.body.code === 'CONFLICT' ? 'warn' : 'neg'} title={failure.body.message}>
            {failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}
          </Alert>
        )}
        <RadioGroup inline label={t('maintenance.dialog.when')} value={when} onChange={v => setWhen(v as 'now' | 'planned')}
          options={[{ value: 'now', label: t('maintenance.dialog.now') }, { value: 'planned', label: t('maintenance.dialog.planned') }]} />
        {when === 'planned' && (
          <DateTimePicker label={t('maintenance.dialog.start')} value={start} onChange={setStart} min={new Date()}
            max={new Date(Date.now() + (bounds?.maxDaysAhead ?? 30) * 86_400_000)} step={15} error={fieldError('start')} />
        )}
        <Stack gap={8}>
          <Field label={t('maintenance.dialog.duration')} required>
            <Segmented label={t('maintenance.dialog.duration')} size="sm" value={duration} onChange={setDuration}
              items={[...DURATIONS.map(d => ({ id: d, label: durationLabel(Number(d)) })), { id: 'other', label: t('maintenance.dialog.otherDuration') }]} />
          </Field>
          {duration === 'other' && (
            <NumberInput label={t('maintenance.dialog.otherMinutes')} unit="min" value={other} onChange={setOther}
              min={bounds?.minMinutes ?? 15} max={bounds?.maxMinutes ?? 1440} step={15} error={fieldError('durationMinutes')} />
          )}
        </Stack>
        <Textarea label={t('maintenance.dialog.reason')} value={reason} onChange={setReason} required maxLength={bounds?.reasonMax ?? 500}
          hint={t('maintenance.dialog.reasonHint', { n: reasonMin })} error={fieldError('reason')} />
        <Stack gap={8}>
          <Checkbox checked disabled label={t('maintenance.dialog.suspend')} />
          <Checkbox checked={notifyTeam} onChange={setNotifyTeam}
            label={teams.length === 1 ? t('maintenance.dialog.notifyTeam', { team: teams[0]! }) : t('maintenance.dialog.notifyTeams')} />
          <Checkbox checked={keepSecurity} onChange={setKeepSecurity} label={t('maintenance.dialog.keepSecurity')} />
        </Stack>
        {!keepSecurity && <Alert tone="warn">{t('maintenance.dialog.securityWarning')}</Alert>}
      </Stack>
    </Dialog>
  );
}

export function durationLabel(minutes: number): string {
  if (minutes < 60) return t('maintenance.minutes', { n: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? t('maintenance.hoursMinutes', { h, m }) : t('maintenance.hours', { n: h });
}
